// client/src/components/FeaturedProducts.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ProductCard from "./ProductCard";
import { useCatalog } from "../hooks/useCatalog"; // uses /api/catalog

const FeaturedProducts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const { products: stripeProducts = [], loading, error } = useCatalog();

  // Map Stripe response to what ProductCard expects + add display fields
  const products = useMemo(() => {
    return (stripeProducts || []).map((p) => {
      const sorted = [...(p.sizes || [])].sort(
        (a, b) => (a.unitAmount ?? 0) - (b.unitAmount ?? 0)
      );

      const sizes = sorted.map((s) => s.label);
      const price = {};
      const priceIdBySize = {};
      const currencyBySize = {};

      sorted.forEach((s) => {
        if (!s?.label) return;
        price[s.label] = (Number(s.unitAmount || 0) / 100).toFixed(2);
        priceIdBySize[s.label] = s.priceId;
        currencyBySize[s.label] = s.currency;
      });

      const first = sorted[0] || null;

      return {
        ...p,
        sizes, // ["5oz","12oz"]
        price, // { "5oz":"7.99", ... }
        priceIdBySize, // { "5oz":"price_123", ... }
        currencyBySize, // { "5oz":"CAD", ... }
        displayPrice: first ? (first.unitAmount / 100).toFixed(2) : null,
        displayCurrency: first ? first.currency?.toUpperCase() : "CAD",
        featured: !!p.featured,
      };
    });
  }, [stripeProducts]);

  // prefer explicitly featured; fallback to first 4 if none
  let featuredProducts = products.filter((p) => p.featured);
  if (featuredProducts.length === 0) featuredProducts = products.slice(0, 4);

  const [startIndex, setStartIndex] = useState(0);
  const itemsToShow = isMobile ? 1 : isTablet ? 2 : 4;
  const maxIndex = Math.max(0, featuredProducts.length - itemsToShow);

  const scrollLeft = () =>
    setStartIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  const scrollRight = () =>
    setStartIndex((prev) => (prev === maxIndex ? 0 : prev + 1));

  // reset/clamp when layout or data changes
  useEffect(() => {
    setStartIndex((idx) =>
      Math.min(idx, Math.max(0, featuredProducts.length - itemsToShow))
    );
  }, [itemsToShow, featuredProducts.length]);

  // autoplay only if we actually have pages to scroll
  useEffect(() => {
    if (maxIndex === 0) return;
    const t = setInterval(scrollRight, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxIndex]);

  const visible = featuredProducts.slice(startIndex, startIndex + itemsToShow);

  if (loading) {
    return (
      <Grid container justifyContent="center">
        <Grid size={{ xs: 12 }}>
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography variant="h6">Loading products…</Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container justifyContent="center">
        <Grid size={{ xs: 12 }}>
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography variant="h6">Failed to load products.</Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center">
      <Grid size={{ xs: 12 }}>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 4 }}>
            Featured Products
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {maxIndex > 0 && (
              <IconButton onClick={scrollLeft} aria-label="previous products">
                <ArrowBackIos />
              </IconButton>
            )}

            <Grid
              container
              spacing={3}
              justifyContent="center"
              sx={{ maxWidth: 1200, mx: "auto" }}
            >
              {visible.map((p) => (
                <Grid key={p.id} size={{ xs: 12, sm: 6, md: 3 }}>
                  <ProductCard product={p} />
                </Grid>
              ))}
            </Grid>

            {maxIndex > 0 && (
              <IconButton onClick={scrollRight} aria-label="next products">
                <ArrowForwardIos />
              </IconButton>
            )}
          </Box>

          {maxIndex > 0 && (
            <Box mt={3}>
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setStartIndex(i)}
                  sx={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: i === startIndex ? "#333" : "#ccc",
                    mx: 0.5,
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default FeaturedProducts;
